import { useRef } from 'react';
import { FaWhatsapp, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { useStore, KEYS, waLink, telLink, prettyPhone } from "../admin/store";
import { Helmet } from "react-helmet";


const Contact = () => {
    const formRef = useRef(null);
    const info = useStore(KEYS.settings);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData(formRef.current);
        const data = Object.fromEntries(formData.entries());

        try {
            const response = await fetch('https://formspree.io/f/xzzvlaej', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(data),
            });

            if (response.ok) {
                alert('Your request has been sent successfully!');
                formRef.current.reset();
            } else {
                alert('There was an error submitting your form. Please try again.');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('There was an error submitting your form. Please try again.');
        }
    };

    return (
        <><Helmet>
            <title>Contact Us | MM AutoTech</title>
            <meta
                name="description"
                content="Get in touch with MM AutoTech for enquiries about our garage support, digital marketing, and business growth services."
            />
            <link rel="canonical" href="https://mmautotech.com/contact" />
        </Helmet>
            <div className="bg-gray-50">
                {/* Hero Section with Intro Text */}
                <div className="relative w-full h-[450px] md:h-[500px]">
                    <img
                        src="/assets/contact/1.jpg"
                        alt="Garage Services"
                        className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center text-center px-4">
                        <h1 className="text-white text-4xl md:text-5xl font-bold mb-4">
                            Your Trusted Garage Support Partner
                        </h1>
                        <p className="text-gray-200 text-lg md:text-xl max-w-3xl">
                            At MM Autotech, we specialize in delivering tailored support services to meet your unique needs. Whether you require expert customer service, reliable parts procurement, strategic sales assistance, targeted PPC campaigns, or impactful digital marketing solutions, our team is here to help. With a commitment to quality and efficiency
                        </p>
                    </div>
                </div>

                {/* Contact Section */}
                <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12">
                    {/* Contact Form */}
                    <div className="bg-white p-8 shadow-xl rounded-2xl animate-slide-up">
                        <h2 className="text-2xl font-bold mb-6 text-gray-800">Send Us a Message</h2>
                        <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                                <input
                                    name="name"
                                    type="text"
                                    placeholder="Your Name"
                                    required
                                    className="mt-1 block w-full border border-gray-300 rounded-lg px-4 py-2 shadow-sm focus:ring-blue-500 focus:border-blue-500" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email Address</label>
                                <input
                                    name="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    required
                                    className="mt-1 block w-full border border-gray-300 rounded-lg px-4 py-2 shadow-sm focus:ring-blue-500 focus:border-blue-500" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                                <input
                                    name="phone"
                                    type="tel"
                                    placeholder="+44 123 456789"
                                    required
                                    className="mt-1 block w-full border border-gray-300 rounded-lg px-4 py-2 shadow-sm focus:ring-blue-500 focus:border-blue-500" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Message</label>
                                <textarea
                                    name="message"
                                    rows="4"
                                    placeholder="How can we help you?"
                                    required
                                    className="mt-1 block w-full border border-gray-300 rounded-lg px-4 py-2 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                ></textarea>
                            </div>

                            <button
                                type="submit"
                                className="w-full bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition duration-300"
                            >
                                Send Message
                            </button>
                        </form>
                    </div>

                    {/* Office Info */}
                    <div className="space-y-8 animate-slide-up delay-300">
                        <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition">
                            <h3 className="text-xl font-semibold mb-3 text-gray-800">Our Office</h3>
                            <p className="text-gray-600 flex gap-3">
                                <FaMapMarkerAlt className="text-blue-600 mt-1 flex-shrink-0" />
                                <span>
                                    {info.companyName}<br />
                                    {info.addressLine1}<br />
                                    {info.addressLine2}
                                </span>
                            </p>
                            <p className="text-gray-600 mt-3 pl-7">
                                {info.hoursWeekdays}<br />
                                {info.hoursWeekend}
                            </p>
                        </div>

                        <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition">
                            <h3 className="text-xl font-semibold mb-3 text-gray-800">Contact</h3>
                            <div className="space-y-3 text-gray-600">
                                <a href={telLink(info.whatsapp)} className="flex items-center gap-3 hover:text-blue-700">
                                    <FaPhoneAlt className="text-blue-600" />
                                    {prettyPhone(info.whatsapp)}
                                </a>
                                <a href={`mailto:${info.email}`} className="flex items-center gap-3 hover:text-blue-700 break-all">
                                    <FaEnvelope className="text-blue-600" />
                                    {info.email}
                                </a>
                            </div>
                            <a
                                href={waLink(info.whatsapp)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-5 inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-5 py-2.5 rounded-lg transition"
                            >
                                <FaWhatsapp className="text-xl" />
                                Chat on WhatsApp: {prettyPhone(info.whatsapp)}
                            </a>
                        </div>

                        <div className="rounded-2xl shadow-lg">
                            <img
                                src="/assets/contact/2.png"
                                alt="MM AutoTech Location"
                                className="w-full h-[250px] object-cover rounded-2xl"
                                loading="lazy" />
                        </div>

                    </div>
                </div>


            </div></>
    );
};

export default Contact;